import React, { useCallback, useMemo } from 'react';
import { WidgetPoints } from '../../../modules/points/types/pointsWidgetstypes';
import PointsUI from 'src/components/ui/pointsUI';
import { PointsProps } from '../../../modules/points/types/types';
import { Grid, Typography } from '@material-ui/core';
import IntegerInput from 'src/components/ui/pointsUI/integerInputBase';
import { getPointId } from 'src/components/modules/points/helpers';

interface TimeWidgetIntegerListProps {
  inputValues: Record<string, string | number | string[]>;
  points: WidgetPoints[];
  onChange: (point: PointsProps, value: string | number | string[]) => void;
  title: string;
}

const DATE_ORDER = ['Month', 'Day', 'Year'] as const;
const TIME_ORDER = ['Hour', 'Minute', 'Second'] as const;

const convertBinaryToDecimal = (binaryArray: string[]): number => {
  return parseInt(binaryArray.reverse().join(''), 2);
};

const convertDecimalToBinary = (decimal: number, length: number): string[] => {
  return (decimal || 0).toString(2).padStart(length, '0').split('');
};

const getTimeWidgetSeparator = (label?: string): string => {
  const isDate = label?.toLowerCase().match(/(year|month|day)/);
  return isDate ? '/' : ':';
};

// TODO: reuse function for integer list
const TimeWidgetIntegerList: React.FC<TimeWidgetIntegerListProps> = ({
  inputValues,
  points,
  onChange,
  title,
}) => {
  const renderInput = useCallback(
    (key: string, label: string, values: string[], point: PointsProps) => {
      const indexStart = Number(key.split('-')[0]) || 0;
      const indexEnd = Number(key.split('-')[1]) || indexStart + 1;
      const binaryValues = values.slice(indexStart, indexEnd + 1);
      const decimalValue = convertBinaryToDecimal(binaryValues);

      const onKeyChange = (key: string, newValue: number, values: string[], point: PointsProps) => {
        const indexStart = Number(key.split('-')[0]) || 0;
        const indexEnd = Number(key.split('-')[1]) || indexStart + 1;
        const length = indexEnd - indexStart + 1;

        const newValueBinary = convertDecimalToBinary(newValue, length);
        const newValues = [...values];
        newValues.splice(indexStart, length, ...newValueBinary.reverse());

        onChange(point, convertBinaryToDecimal(newValues));
      };

      return (
        <IntegerInput
          onKeyChange={(key, newValue) => onKeyChange(key, newValue, values, point)}
          keyData={key}
          point={point}
          value={decimalValue}
          label={label}
        />
      );
    },
    [onChange]
  );

  const allFields = useMemo(
    () =>
      points.reduce((acc, point: WidgetPoints) => {
        const values = inputValues[getPointId(point)]
          ?.toString(2)
          .padStart(16, '0')
          .split('')
          .reverse();

        const fields: { key: string; label: string; values: string[]; point: WidgetPoints }[] =
          Object.entries(point.entry.meanings || {}).map(([key, label]) => ({
            key,
            label,
            values,
            point,
          }));

        return acc.concat(fields);
      }, [] as { key: string; label: string; values: string[]; point: WidgetPoints }[]),
    [points, inputValues]
  );

  const { dateFields, timeFields } = useMemo(
    () => ({
      dateFields: allFields
        .filter((field) => field.label.toLowerCase().match(/(year|month|day)/))
        .sort(
          (a, b) =>
            DATE_ORDER.indexOf(a.label as (typeof DATE_ORDER)[number]) -
            DATE_ORDER.indexOf(b.label as (typeof DATE_ORDER)[number])
        ),
      timeFields: allFields
        .filter((field) => field.label.toLowerCase().match(/(hour|minute|second)/))
        .sort(
          (a, b) =>
            TIME_ORDER.indexOf(a.label as (typeof TIME_ORDER)[number]) -
            TIME_ORDER.indexOf(b.label as (typeof TIME_ORDER)[number])
        ),
    }),
    [allFields]
  );

  const renderInputGroup = useCallback(
    (fields: typeof allFields) => (
      <Grid container alignItems="center">
        {fields.map((field, index) => (
          <React.Fragment key={field.key}>
            <Grid item xs>
              {renderInput(field.key, field.label, field.values, field.point)}
            </Grid>
            {index < fields.length - 1 && (
              <Grid item style={{ width: '18px' }}>
                <Typography variant="body1" style={{ textAlign: 'center' }}>
                  {getTimeWidgetSeparator(field.label)}
                </Typography>
              </Grid>
            )}
          </React.Fragment>
        ))}
      </Grid>
    ),
    [renderInput]
  );

  return (
    <PointsUI title={title}>
      <Grid container direction="column" spacing={2} style={{ marginTop: 10 }}>
        {dateFields.length > 0 && <Grid item>{renderInputGroup(dateFields)}</Grid>}
        {timeFields.length > 0 && <Grid item>{renderInputGroup(timeFields)}</Grid>}
      </Grid>
    </PointsUI>
  );
};

export default TimeWidgetIntegerList;
